import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, ArrowLeft, Shield } from "lucide-react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Input from "../ui/Input";
import OTPInput from "./OTPInput";
import { useAuth } from "../../hooks/useAuth";
import { ROLES } from "../../config/constants";

const STEPS = { MOBILE: "mobile", OTP: "otp", NAME: "name" };

export default function LoginModal({ isOpen, onClose, redirectTo, onNewUser }) {
  const [step, setStep] = useState(STEPS.MOBILE);
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [mobileError, setMobileError] = useState("");
  const { sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();

  const navigateAfterLogin = (userRole) => {
    if (userRole === ROLES.ADMIN) navigate("/admin");
    else if (userRole === ROLES.TECHNICIAN) navigate("/technician");
    else if (userRole === ROLES.CHANNEL_PARTNER) navigate("/partner");
    else if (redirectTo) navigate(redirectTo);
  };

  const resetAndClose = () => {
    setStep(STEPS.MOBILE);
    setMobile("");
    setName("");
    setMobileError("");
    onClose();
  };

  const handleSendOtp = async () => {
    console.log("STEP 1: Button clicked");
    const cleaned = mobile.replace(/\D/g, "");
    console.log("STEP 2: cleaned mobile =", cleaned);
    if (cleaned.length !== 10) {
      console.log("STEP 3: validation failed");
      setMobileError("Enter a valid 10-digit mobile number");
      return;
    }
    setMobileError("");
    console.log("STEP 4: calling mutate");
    sendOtp.mutate(
      { mobile: cleaned },
      {
        onSuccess: () => {
          console.log("STEP 7: SUCCESS");
          setStep(STEPS.OTP);
        },

        onError: (err) => {
          console.log("STEP 7: ERROR", err);
          setMobileError(err?.response?.data?.message || "Failed to send OTP");
        },
      },
    );
  };

  const handleVerifyOtp = async (otp) => {
    console.log("STEP V1: verify clicked");
    console.log("OTP:", otp);
    console.log("Mobile:", mobile);
    verifyOtp.mutate(
      { mobile: mobile.replace(/\D/g, ""), otp },
      {
        onSuccess: (res) => {
          console.log("STEP V4: SUCCESS", res);
          const data = res.data;
          const userRole = data?.user?.role || data?.role;
          const isNew = data?.user?.isNewUser || data?.isNewUser;
          resetAndClose();
          navigateAfterLogin(userRole);
          if (isNew && onNewUser) {
            setTimeout(() => onNewUser(), 300);
          }
        },
        onError: (err) => {
          console.log("STEP V4: ERROR", err);
        },
      },
    );
  };

  const handleSetName = () => {
    // Name update handled via profile API after login
    if (redirectTo) navigate(redirectTo);
    resetAndClose();
  };

  return (
    <Modal open={isOpen} onClose={resetAndClose} title="" maxWidth="sm">
      <div className="px-2 pb-2">
        {/* Mobile Step */}
        {step === STEPS.MOBILE && (
          <div className="space-y-6">
            <div className="text-center">
              <img
                src="/dm_logo-updated.png"
                alt="MechOnGo Automobiles"
                className="h-20 w-auto mx-auto mb-3"
              />
              <h2 className="text-xl font-bold text-neutral-900">
                Welcome to MechOnGo Automobiles
              </h2>
              <p className="text-sm text-neutral-500 mt-1">
                Enter your mobile number to continue
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center bg-neutral-100 border border-neutral-300 rounded-lg h-11 px-3 text-sm font-medium text-neutral-700">
                +91
              </div>
              <Input
                type="tel"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  setMobileError("");
                }}
                error={mobileError}
                maxLength={10}
                inputMode="numeric"
                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
              />
            </div>

            <Button
              className="w-full"
              onClick={handleSendOtp}
              loading={sendOtp.isPending}
              disabled={mobile.replace(/\D/g, "").length < 10}
            >
              Send OTP
            </Button>

            <p className="text-xs text-neutral-400 text-center">
              By continuing, you agree to our Terms of Service and Privacy
              Policy
            </p>
          </div>
        )}

        {/* OTP Step */}
        {step === STEPS.OTP && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                <Shield className="text-primary-600" size={28} />
              </div>
              <h2 className="text-xl font-bold text-neutral-900">Verify OTP</h2>
              <p className="text-sm text-neutral-500 mt-1">
                Enter the 6-digit code sent to +91 {mobile}
              </p>
            </div>

            <OTPInput
              onComplete={handleVerifyOtp}
              disabled={verifyOtp.isPending}
            />

            {verifyOtp.isError && (
              <p className="text-sm text-error-600 text-center">
                {verifyOtp.error?.response?.data?.message ||
                  "Invalid OTP. Please try again."}
              </p>
            )}

            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep(STEPS.MOBILE)}
                className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"
              >
                <ArrowLeft size={14} />
                Change number
              </button>

              <button
                onClick={handleSendOtp}
                disabled={sendOtp.isPending}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
              >
                Resend OTP
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
